import { Video } from "../../Store/Reducers/VideoSlice";
import {
  ACCOUNT_TEAM_LOGIN_ROLE_LIST,
  ACCOUNT_TEAM_ROLE_FILTER,
  ROLE_DISPLAY_NAMES,
  ROLES,
  type AccountTeamLoginRoleType,
} from "../../Utils/Constants/RoleConstants";

/** Canonical role key for assignment grouping (one bucket per login role). */
export const getVideoRoleKey = (role?: string | null): string => role || "";

const videoRoleLabel = (role: string): string => {
  if (ACCOUNT_TEAM_LOGIN_ROLE_LIST.includes(role as AccountTeamLoginRoleType)) {
    return role.toUpperCase();
  }
  return ROLE_DISPLAY_NAMES[role as keyof typeof ROLE_DISPLAY_NAMES] || role;
};

const isAccountTeamLoginRole = (role: string): boolean =>
  ACCOUNT_TEAM_LOGIN_ROLE_LIST.includes(role as AccountTeamLoginRoleType);

/** Concrete login roles used for assign / reorder / library badges. */
export const VIDEO_ROLE_ACCORDIONS = Object.values(ROLES)
  .filter(role => role !== ROLES.IZBA_ADMIN)
  .map(role => ({
    value: role,
    label: videoRoleLabel(role),
  }));

/** Top Role Assignments dropdown: non–account-team roles, then Account Team last. */
export const VIDEO_ROLE_TOP_OPTIONS = [
  ...Object.values(ROLES)
    .filter(role => role !== ROLES.IZBA_ADMIN && !isAccountTeamLoginRole(role))
    .map(role => ({
      value: role,
      label: videoRoleLabel(role),
    })),
  { value: ACCOUNT_TEAM_ROLE_FILTER, label: "Account Team" },
];

/** Legacy value — no longer offered in the Subrole dropdown. */
export const VIDEO_ACCOUNT_TEAM_ALL_SUBROLE = "all";

/** Subroles shown after Account Team is selected (concrete roles only). */
export const VIDEO_ACCOUNT_TEAM_SUBROLE_OPTIONS =
  ACCOUNT_TEAM_LOGIN_ROLE_LIST.map(role => ({
    value: role,
    label: videoRoleLabel(role),
  }));

/** Default Account Team browse subrole — first concrete role (AE). */
export const DEFAULT_ACCOUNT_TEAM_SUBROLE =
  ACCOUNT_TEAM_LOGIN_ROLE_LIST[0] || "ae";

export const ACCOUNT_TEAM_ASSIGNMENT_ROLES: readonly string[] = [
  ...ACCOUNT_TEAM_LOGIN_ROLE_LIST,
];

export const isVideoAccountTeamTopRole = (roleKey: string): boolean =>
  roleKey === ACCOUNT_TEAM_ROLE_FILTER;

export const isVideoAccountTeamAllSubrole = (subRole: string): boolean =>
  subRole === VIDEO_ACCOUNT_TEAM_ALL_SUBROLE;

export const sortRolesByVideoCount = (
  roles: typeof VIDEO_ROLE_ACCORDIONS,
  videosByRole: Record<string, Video[]>
) =>
  [...roles].sort((a, b) => {
    const countDiff =
      (videosByRole[b.value]?.length ?? 0) -
      (videosByRole[a.value]?.length ?? 0);
    if (countDiff !== 0) return countDiff;
    return a.label.localeCompare(b.label);
  });

/** Preserve API order; only ensure video_order has a fallback value. */
export const normalizeVideos = (videos: Video[]): Video[] =>
  videos.map(video => ({
    ...video,
    video_order: video.video_order ?? -1,
  }));

export const recalculateVideoOrders = (videos: Video[]): Video[] => {
  let activeCounter = 1;
  return videos.map(video => {
    if (video.is_active ?? true) {
      const updated = { ...video, video_order: activeCounter };
      activeCounter += 1;
      return updated;
    }
    return { ...video, video_order: -1 };
  });
};

export const moveVideoToIndex = (
  videos: Video[],
  fromIndex: number,
  toIndex: number
): Video[] => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= videos.length ||
    toIndex >= videos.length
  ) {
    return videos;
  }

  const next = [...videos];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const reorderVideos = (
  videos: Video[],
  draggedId: string,
  targetId: string
): Video[] => {
  if (draggedId === targetId) return videos;

  const draggedVideo = videos.find(video => video.video_id === draggedId);
  if (!draggedVideo || !(draggedVideo.is_active ?? true)) {
    return videos;
  }

  const fromIndex = videos.findIndex(video => video.video_id === draggedId);
  const toIndex = videos.findIndex(video => video.video_id === targetId);
  if (fromIndex === -1 || toIndex === -1) return videos;

  return recalculateVideoOrders(moveVideoToIndex(videos, fromIndex, toIndex));
};

export const haveVideoOrdersChanged = (
  original: Video[],
  updated: Video[]
): boolean => {
  if (original.length !== updated.length) return true;

  return updated.some((video, index) => {
    const source = original[index];
    return (
      source.video_id !== video.video_id ||
      (source.video_order ?? -1) !== (video.video_order ?? -1)
    );
  });
};

export const cloneVideos = (videos: Video[]): Video[] =>
  videos.map(video => ({ ...video }));
